import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Save } from "lucide-react";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import { SuccessModal } from "../../components/modal";
import PerformanceStatusBadge from "../../components/performance/PerformanceStatusBadge";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";

const getMessage = (error, fallback) => {
  const responseMessage = error?.response?.data?.message || fallback;
  return Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage;
};

const numberOnly = (value) => `${value || ""}`.replace(/[^\d]/g, "");

const getScoreValue = (scores, key, kpiId) => {
  const score = scores?.[key]?.[kpiId];
  return score === 0 || score ? `${score}` : "";
};

const getKpiAverageTotal = (scores, kpiId, maxScore) => {
  const values = ["self", "lineManagerOne", "lineManagerTwo"]
    .map((key) => getScoreValue(scores, key, kpiId))
    .filter((value) => value !== "")
    .map((value) => Number(value) || 0);

  if (values.length === 0) {
    return 0;
  }

  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  return Math.min(Number(maxScore) || 0, Math.round(average));
};

export default function PerformanceEvaluationDetails() {
  const { id } = useParams();
  const [evaluation, setEvaluation] = useState(null);
  const [draftScores, setDraftScores] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [message, setMessage] = useState("");
  const [rejectForm, setRejectForm] = useState({ scoreKey: "", reason: "", triggerConflict: true });
  const apiBaseUrl = getApiBaseUrl();
  const navigate = useNavigate();

  const loadEvaluation = useCallback(async () => {
    if (!apiBaseUrl || !id) {
      setMessage("Unable to load this KPI evaluation.");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await axios.get(`${apiBaseUrl}/organizations/performance-evaluations/${id}`);
      const nextEvaluation = response?.data?.evaluation || null;
      setEvaluation(nextEvaluation);
      setDraftScores(nextEvaluation?.scores || {});
      setMessage("");
    } catch (error) {
      setMessage(getMessage(error, "Unable to load performance evaluation."));
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, id]);

  useEffect(() => {
    void loadEvaluation();
  }, [loadEvaluation]);

  useEffect(() => {
    if (!showSuccessModal) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      navigate("/performance-evaluation");
    }, 1600);

    return () => window.clearTimeout(timeout);
  }, [navigate, showSuccessModal]);

  const editableKey = useMemo(() => {
    if (evaluation?.canEditSelf) return "self";
    if (evaluation?.canEditLineManagerOne) return "lineManagerOne";
    if (evaluation?.canEditLineManagerTwo) return "lineManagerTwo";
    return "";
  }, [evaluation]);

  const editableLabel = {
    self: "Self score",
    lineManagerOne: "Line manager 1",
    lineManagerTwo: "Line manager 2",
  }[editableKey];

  const updateScore = (scoreKey, kpiId, value, maxScore) => {
    if (!scoreKey || scoreKey !== editableKey) {
      return;
    }

    const cleanValue = numberOnly(value);
    const cappedValue =
      cleanValue === ""
        ? ""
        : `${Math.min(Number(cleanValue), Number(maxScore) || 0)}`;

    setDraftScores((current) => ({
      ...current,
      [scoreKey]: {
        ...(current?.[scoreKey] || {}),
        [kpiId]: cappedValue,
      },
    }));
  };

  const handleSave = async () => {
    if (!editableKey) return;

    const sections = Array.isArray(evaluation?.sections) ? evaluation.sections : [];
    const scores = [];

    for (const section of sections) {
      for (const kpi of Array.isArray(section.kpis) ? section.kpis : []) {
        const score = Number(draftScores?.[editableKey]?.[kpi.id] || 0);
        const maxScore = Number(kpi.maxScore) || 0;

        if (score < 0 || score > maxScore) {
          setMessage(`Score for ${kpi.id} must be between 0 and ${maxScore}.`);
          return;
        }

        scores.push({ kpiId: kpi.id, score });
      }
    }

    try {
      setIsSaving(true);
      setMessage("");
      const response = await axios.patch(`${apiBaseUrl}/organizations/performance-evaluations/${id}/scores`, {
        scores,
      });
      const nextEvaluation = response?.data?.evaluation || null;
      setEvaluation(nextEvaluation);
      setDraftScores(nextEvaluation?.scores || {});
      setShowSuccessModal(true);
    } catch (error) {
      setMessage(getMessage(error, "Unable to save performance scores."));
    } finally {
      setIsSaving(false);
    }
  };

  const openRejectForm = (scoreKey) => {
    setRejectForm({ scoreKey, reason: "", triggerConflict: true });
    setMessage("");
  };

  const handleRejectGrade = async () => {
    if (!rejectForm.scoreKey || !rejectForm.reason.trim()) {
      setMessage("Enter a reason before rejecting the grade.");
      return;
    }

    try {
      setIsRejecting(true);
      setMessage("");
      const response = await axios.patch(`${apiBaseUrl}/organizations/performance-evaluations/${id}/scores/reject`, {
        scoreKey: rejectForm.scoreKey,
        reason: rejectForm.reason.trim(),
        triggerConflict: rejectForm.triggerConflict,
      });
      const nextEvaluation = response?.data?.evaluation || null;
      setEvaluation(nextEvaluation);
      setDraftScores(nextEvaluation?.scores || {});
      setRejectForm({ scoreKey: "", reason: "", triggerConflict: true });
      setMessage(response?.data?.message || "KPI grade rejected successfully.");
    } catch (error) {
      setMessage(getMessage(error, "Unable to reject performance grade."));
    } finally {
      setIsRejecting(false);
    }
  };

  const handleAcknowledgeGrades = async () => {
    try {
      setIsResponding(true);
      setMessage("");
      const response = await axios.patch(`${apiBaseUrl}/organizations/performance-evaluations/${id}/grade-response`, {
        response: "acknowledged",
      });
      const nextEvaluation = response?.data?.evaluation || null;
      setEvaluation(nextEvaluation);
      setDraftScores(nextEvaluation?.scores || {});
      setMessage(response?.data?.message || "Manager grades acknowledged successfully.");
    } catch (error) {
      setMessage(getMessage(error, "Unable to acknowledge manager grades."));
    } finally {
      setIsResponding(false);
    }
  };

  const sections = Array.isArray(evaluation?.sections) ? evaluation.sections : [];
  const rejections = evaluation?.rejections || {};
  const canRespondToGrades = evaluation?.canRespondToManagerGrades;
  const gradeResponse = evaluation?.gradeResponse;
  const isPositiveMessage =
    message.includes("success") || message.includes("created") || message.includes("rejected");

  return (
    <div className="max-w-8xl mx-auto p-4 sm:p-6 font-Nunito">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-black">Performance Evaluation</h1>
          <p className="mt-1 text-sm text-slate-500">
            {evaluation?.employee?.fullName || "Staff KPI"} {evaluation?.role?.name ? `- ${evaluation.role.name}` : ""}
          </p>
        </div>
        <Link to="/performance-evaluation" className="text-sm font-semibold text-blue-600">
          Back to list
        </Link>
      </div>

      <div className="bg-white p-3 sm:p-6">
        {message ? (
          <div className={`mb-5 rounded-lg border px-4 py-3 text-sm ${isPositiveMessage ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}`} role="alert">
            {message}
          </div>
        ) : null}

        {isLoading ? (
          <FetchLoadingState message="Loading evaluation..." className="min-h-[360px]" />
        ) : evaluation ? (
          <>
            <div className="mb-5 grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-3 sm:p-4 md:mb-3 md:grid-cols-6 md:gap-4">
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500">Staff</p>
                <p className="mt-1 font-semibold text-black">{evaluation.employee?.fullName || "-"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500">Department</p>
                <p className="mt-1 font-semibold text-black">{evaluation.department?.name || "-"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500">Frequency</p>
                <p className="mt-1 font-semibold text-black">{evaluation.frequency || "-"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500">Editable column</p>
                <p className="mt-1 font-semibold text-black">{editableLabel || "No scoring window open"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500">Progress</p>
                <p className="mt-1 font-semibold text-black">{evaluation.progressLabel || `${evaluation.completionPercent || 0}% completed`}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500">Performance status</p>
                <div className="mt-1"><PerformanceStatusBadge code={evaluation.performanceStatusCode} label={evaluation.performanceStatus} reason={evaluation.performanceStatusReason} /></div>
              </div>
            </div>

            <div className="mb-6 flex items-start gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
              <PerformanceStatusBadge code={evaluation.performanceStatusCode} label={evaluation.performanceStatus} compact />
              <p className="leading-5">{evaluation.performanceStatusReason || "Performance health will update as evaluation responses and scores are submitted."}</p>
            </div>

            <div className="space-y-5">
              {sections.map((section, sectionIndex) => (
                <div key={section.id || sectionIndex} className="rounded-lg border border-slate-200 p-3 sm:p-5">
                  <h2 className="mb-5 text-lg font-semibold text-black">
                    {sectionIndex + 1}. {section.title}
                  </h2>

                  <div className="mb-3 grid grid-cols-[58px,1fr,360px] gap-3 text-xs font-semibold uppercase text-slate-500 max-lg:hidden">
                    <span>No</span>
                    <span>Key performance indicator</span>
                    <span className="grid grid-cols-4 gap-2 text-center">
                      <span>Self</span>
                      <span>Manager 1</span>
                      <span>Manager 2</span>
                      <span>Total</span>
                    </span>
                  </div>

                  <div className="space-y-3">
                    {(section.kpis || []).map((kpi, kpiIndex) => {
                      const maxScore = Number(kpi.maxScore) || 0;
                      const rowTotal = getKpiAverageTotal(draftScores, kpi.id, maxScore);

                      return (
                        <div key={kpi.id} className="grid gap-3 lg:grid-cols-[58px,1fr,360px] lg:items-center">
                          <span className="text-sm font-semibold text-black">{sectionIndex + 1}.{kpiIndex + 1}</span>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{kpi.text}</p>
                            <p className="text-xs text-slate-500">Maximum score: {maxScore}</p>
                          </div>
                          <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                            <div className="col-span-4 grid grid-cols-4 gap-1.5 text-center text-[10px] font-semibold uppercase text-slate-400 sm:gap-2 lg:hidden">
                              <span>Self</span><span>M1</span><span>M2</span><span>Total</span>
                            </div>
                            {["self", "lineManagerOne", "lineManagerTwo"].map((key) => (
                              <input
                                key={key}
                                type="text"
                                value={getScoreValue(draftScores, key, kpi.id)}
                                disabled={editableKey !== key}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                onChange={(event) => updateScore(key, kpi.id, event.target.value, maxScore)}
                                className={`h-11 rounded-md border text-center text-sm outline-none ${
                                  editableKey === key
                                    ? "border-blue-400 bg-white text-slate-900 focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                                    : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-500"
                                }`}
                              />
                            ))}
                            <input
                              value={rowTotal}
                              readOnly
                              className="h-11 rounded-md border border-slate-200 bg-slate-50 text-center text-sm font-semibold text-black"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {evaluation.remarks ? (
              <div className="mt-6 rounded-lg bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase text-slate-500">Remarks</p>
                <p className="mt-2 text-sm text-slate-700">{evaluation.remarks}</p>
              </div>
            ) : null}

            {canRespondToGrades ? (
              <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-amber-900">Grade review</p>
                    <p className="mt-1 text-xs text-amber-800">Review submitted manager grades, acknowledge them, or dispute a specific grade.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleAcknowledgeGrades}
                      disabled={isResponding || gradeResponse?.response === "acknowledged"}
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {gradeResponse?.response === "acknowledged" ? "Grades acknowledged" : isResponding ? "Saving..." : "Acknowledge grades"}
                    </button>
                    {evaluation.canRejectLineManagerOne ? (
                      <button
                        type="button"
                        onClick={() => openRejectForm("lineManagerOne")}
                        className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-900"
                      >
                        Reject Manager 1
                      </button>
                    ) : null}
                    {evaluation.canRejectLineManagerTwo ? (
                      <button
                        type="button"
                        onClick={() => openRejectForm("lineManagerTwo")}
                        className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-900"
                      >
                        Reject Manager 2
                      </button>
                    ) : null}
                  </div>
                </div>

                {gradeResponse?.response === "acknowledged" ? (
                  <p className="mt-4 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                    You acknowledged the manager grades{gradeResponse.respondedAt ? ` on ${new Date(gradeResponse.respondedAt).toLocaleDateString()}` : ""}.
                  </p>
                ) : null}

                {Object.keys(rejections).length > 0 ? (
                  <div className="mt-4 space-y-2">
                    {Object.entries(rejections).map(([key, rejection]) => (
                      <div key={key} className="rounded-md bg-white px-3 py-2 text-xs text-slate-700">
                        <span className="font-semibold text-slate-900">{rejection.managerLabel || key}:</span> {rejection.reason}
                        {rejection.status ? <span className="ml-2 text-amber-700">({rejection.status})</span> : null}
                      </div>
                    ))}
                  </div>
                ) : null}

                {rejectForm.scoreKey ? (
                  <div className="mt-4 space-y-3 rounded-lg bg-white p-4">
                    <p className="text-sm font-semibold text-slate-900">
                      Reject {rejectForm.scoreKey === "lineManagerOne" ? "Manager 1" : "Manager 2"} grade
                    </p>
                    <textarea
                      value={rejectForm.reason}
                      onChange={(event) => setRejectForm((current) => ({ ...current, reason: event.target.value }))}
                      className="min-h-[96px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                      placeholder="Enter rejection reason"
                    />
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={rejectForm.triggerConflict}
                        onChange={(event) => setRejectForm((current) => ({ ...current, triggerConflict: event.target.checked }))}
                      />
                      Trigger conflict for resolution
                    </label>
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setRejectForm({ scoreKey: "", reason: "", triggerConflict: true })}
                        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleRejectGrade}
                        disabled={isRejecting}
                        className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        {isRejecting ? "Submitting..." : "Submit rejection"}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="mt-6 flex justify-stretch sm:justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={!editableKey || isSaving}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#1234CD] px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                <Save size={16} />
                {isSaving ? "Saving..." : "Save scores"}
              </button>
            </div>
          </>
        ) : (
          <div className="py-10 text-sm text-slate-500">Evaluation not found.</div>
        )}
      </div>

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => navigate("/performance-evaluation")}
        onDone={() => navigate("/performance-evaluation")}
        doneLabel="Back to evaluations"
        modalClassName="h-auto min-h-[320px]"
        bodyClassName="mt-4"
      >
        <div className="text-center py-4">
          <p className="text-2xl font-semibold text-[#071C50]">Scores saved</p>
          <p className="mt-3 text-sm text-slate-600">
            The performance scores were saved successfully. Returning to evaluations...
          </p>
        </div>
      </SuccessModal>
    </div>
  );
}
